import React from 'react';
import PropTypes from 'prop-types';
import { Row } from 'components/shared/Layout';
import { legShape } from 'constants/propTypes';
import { map, groupBy, pipe, values, filter } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { ADDITIONAL_DETAIL_VALUE_OPTIONS } from 'constants/assistance';
import {
  ASSISTANCE_ANIMAL_ADDITIONAL_DETAILS_KEY,
  MEET_AND_ASSIST_ADDITIONAL_DETAILS_KEY,
  PASSENGER_DISABILITY_ADDITIONAL_DETAILS_KEY
} from 'constants/booking';
import productTypes from '../../../../../constants/productTypes';

import { StyledHeaderRow, StyledCell } from './AddOnProduct.styles';

const EMPTY_VALUE = '—';

const DETAIL_KEY_ORDER = [
  PASSENGER_DISABILITY_ADDITIONAL_DETAILS_KEY,
  ASSISTANCE_ANIMAL_ADDITIONAL_DETAILS_KEY,
  MEET_AND_ASSIST_ADDITIONAL_DETAILS_KEY
];

const collectAdditionalDetails = (products) => {
  const details = products.flatMap(({ additionalDetails }) => additionalDetails || []);
  const uniqueDetails = [...new Map(details.map((detail) => [`${detail.key}-${detail.value}`, detail])).values()];

  return uniqueDetails.sort((a, b) => DETAIL_KEY_ORDER.indexOf(a.key) - DETAIL_KEY_ORDER.indexOf(b.key));
};

const DetailValue = ({ additionalDetail: { key, value } }) => {
  const valueTranslationId = ADDITIONAL_DETAIL_VALUE_OPTIONS[key]?.find((option) => option.id === value)?.translationId;

  return valueTranslationId ? <FormattedMessage id={valueTranslationId} /> : <>{value}</>;
};

const AddOnProduct = ({ direction }) => {
  const combineDuplicateProductsWithQuantity = (p) => ({
    product: p[0],
    quantity: p.filter((product) => !product.cancelled).length,
    additionalDetails: collectAdditionalDetails(p)
  });

  const specialProductsByPassengers = pipe(
    filter((i) => i.type === productTypes.DISABILITY_SUPPORT),
    filter((i) => !i.cancelled),
    groupBy((p) => `${p.code}-${p.price}-${p.passengerId}-${p.currency}`),
    values(),
    map(combineDuplicateProductsWithQuantity),
    map((p) => ({ product: p.product, quantity: p.quantity, additionalDetails: p.additionalDetails }))
  )(direction.extraProducts);

  return (
    <div data-testid="addon-products" data-cy="extra-journey-product">
      {specialProductsByPassengers.length > 0 ? (
        <>
          <StyledHeaderRow>
            <StyledCell span={5}>
              <FormattedMessage id="addAssistance.passenger" />
            </StyledCell>
            <StyledCell span={5}>
              <FormattedMessage id="fareProducts.product" />
            </StyledCell>
            <StyledCell span={6}>
              <FormattedMessage id="addAssistance.details" />
            </StyledCell>
          </StyledHeaderRow>

          {specialProductsByPassengers.map(({ product: { passenger, name }, quantity, additionalDetails }) => (
            <div key={`${name}-${passenger.firstName}-${passenger.lastName}`} data-cy="addon-product" data-testid="addon-product">
              <Row>
                <StyledCell span={5}>
                  {passenger.firstName} {passenger.lastName}
                </StyledCell>
                <StyledCell span={5}>
                  {quantity} {name}
                </StyledCell>
                <StyledCell span={6}>
                  {additionalDetails.length > 0
                    ? additionalDetails.map((additionalDetail) => (
                        <div key={`${additionalDetail.key}-${additionalDetail.value}`}>
                          <DetailValue additionalDetail={additionalDetail} />
                        </div>
                      ))
                    : EMPTY_VALUE}
                </StyledCell>
              </Row>
              <hr />
            </div>
          ))}
        </>
      ) : (
        <FormattedMessage id="assistance.noSpecialProducts" />
      )}
    </div>
  );
};

const additionalDetailShape = PropTypes.shape({
  key: PropTypes.string,
  value: PropTypes.string
});

DetailValue.propTypes = {
  additionalDetail: additionalDetailShape.isRequired
};

AddOnProduct.propTypes = {
  direction: legShape
};

export default AddOnProduct;
